import React, { useMemo, useState } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button,
  Input
} from "reactstrap";
import SuiteLayout from "../../_suite/SuiteLayout";
import SecuritySocDashboard from "../../securitySoc";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import IntlMessages from "Util/IntlMessages";
import {
  SUITE_SEC_POLICY_RING,
  INTERACTIVE_CHIP,
  LAYOUT_MAX_WIDTH_280
} from "Constants/reactifyInlineStyles";
import { suiteApi } from "../../../../../api/suiteAdapters";
import { useAsyncResource } from "../../../../../hooks/useAsyncResource";
import { AsyncStateBoundary, SuitePageShell } from "../../_suite/primitives";

function VulnSeverityMatrixPage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.security.listVulns(),
    []
  );
  const buckets = useMemo(() => {
    const list = data || [];
    return {
      critical: list.filter(v => v.cvss >= 9),
      high: list.filter(v => v.cvss >= 7 && v.cvss < 9),
      rest: list.filter(v => v.cvss < 7)
    };
  }, [data]);

  type VulnRow = {
    id: string;
    cvss: number;
    asset: string;
    hoursRemaining?: number | null;
  };

  const Cell = ({
    title,
    color,
    items
  }: {
    title: string;
    color: string;
    items: VulnRow[];
  }) => (
    <Col md="4" className="mb-4">
      <h6 className={`text-${color}`}>{title}</h6>
      {items.map((v: VulnRow) => (
        <Card key={v.id} className="mb-2 border-0 shadow-sm">
          <CardBody className="py-2">
            <div className="d-flex justify-content-between">
              <code className="small">{v.id}</code>
              <Badge color="dark">{v.cvss}</Badge>
            </div>
            <div className="small">{v.asset}</div>
            {v.hoursRemaining != null && (
              <div className="small text-danger mt-1">
                SLA: ~{v.hoursRemaining}h left
              </div>
            )}
            <Button
              tag={Link}
              color="link"
              size="sm"
              className="px-0"
              to={encodeURIComponent(v.id)}
              relative="path"
            >
              Evidence →
            </Button>
          </CardBody>
        </Card>
      ))}
    </Col>
  );

  return (
    <SuitePageShell heading={<IntlMessages id="suite.sec.vulns" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          <Cell title="Critical 9+" color="danger" items={buckets.critical} />
          <Cell title="High 7–9" color="warning" items={buckets.high} />
          <Cell title="Other" color="secondary" items={buckets.rest} />
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function VulnDetailPage() {
  const { id } = useParams();
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.security.getVuln(decodeURIComponent(id ?? "")),
    [id]
  );
  return (
    <div>
      <Row>
        <Colxx xxs="12">
          <BreadcrumbContainer
            heading={<IntlMessages id="suite.sec.vuln-detail" />}
          />
          <Separator className="mb-4" />
        </Colxx>
      </Row>
      <Button tag={Link} color="light" to=".." relative="path" className="mb-3">
        ← Back
      </Button>
      <AsyncStateBoundary loading={loading} error={error} empty={!data}>
        {data && (
          <Row>
            <Col md="7">
              <Card className="mb-3">
                <CardBody>
                  <h4>{data.id}</h4>
                  <p>{data.asset}</p>
                  <Badge className="me-2">CVSS {data.cvss}</Badge>
                  {data.sla && (
                    <Badge color="warning">SLA {data.sla}</Badge>
                  )}
                  {data.dueAt && (
                    <p className="small text-muted mt-2 mb-0">
                      Due {data.dueAt}
                      {data.hoursRemaining != null &&
                        ` · ${data.hoursRemaining}h remaining`}
                    </p>
                  )}
                </CardBody>
              </Card>
            </Col>
            <Col md="5">
              <Card className="bg-dark text-white">
                <CardBody>
                  <CardTitle className="text-white">Evidence pack</CardTitle>
                  <p className="small text-white-50 mb-0">
                    PCAP hashes, package versions, and repro steps would render
                    from your vuln DB.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}
      </AsyncStateBoundary>
    </div>
  );
}

function PoliciesCompliancePage() {
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.security.listPolicies(),
    []
  );
  return (
    <SuitePageShell heading={<IntlMessages id="suite.sec.policies" />}>
      <AsyncStateBoundary loading={loading} error={error}>
        <Row>
          {(data || []).map(p => (
            <Col md="6" key={p.id} className="mb-4">
              <Card className="text-center border-0 shadow-sm py-4">
                <CardBody>
                  <div
                    className="rounded-circle border border-primary mx-auto d-flex align-items-center justify-content-center mb-3"
                    style={SUITE_SEC_POLICY_RING}
                  >
                    <span className="h3 mb-0">{p.compliantPct}%</span>
                  </div>
                  <h6>{p.name}</h6>
                  {(p.controls || []).length > 0 && (
                    <ul className="list-unstyled small text-left mb-0 mt-2">
                      {p.controls.map((c, i) => (
                        <li key={i}>· {c}</li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

function AuditLogExplorerPage() {
  const [q, setQ] = useState("");
  const { data, loading, error } = useAsyncResource(
    () => suiteApi.security.listAudit(),
    []
  );
  const filtered = useMemo(() => {
    const list = data || [];
    const t = q.trim().toLowerCase();
    if (!t) return list;
    return list.filter(
      a =>
        String(a.actor).toLowerCase().includes(t) ||
        String(a.action).toLowerCase().includes(t) ||
        String(a.target).toLowerCase().includes(t) ||
        String(a.severity || "").toLowerCase().includes(t)
    );
  }, [data, q]);

  return (
    <SuitePageShell heading={<IntlMessages id="suite.sec.audit" />}>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Button size="sm" color="primary" outline onClick={() => {}}>
          Export audit bundle (wire)
        </Button>
      </div>
      <div className="d-flex flex-wrap gap-2 mb-3">
        {["policy", "auth", "admin"].map(chip => (
          <Badge
            key={chip}
            color="light"
            className="text-dark px-3 py-2"
            style={INTERACTIVE_CHIP}
            onClick={() => setQ(chip)}
          >
            {chip}
          </Badge>
        ))}
        <Input
          type="search"
          className="ms-auto"
          style={LAYOUT_MAX_WIDTH_280}
          placeholder="Filter actor / action…"
          value={q}
          onChange={e => setQ(e.target.value)}
        />
      </div>
      <AsyncStateBoundary loading={loading} error={error}>
        <Card className="border-0 shadow-sm">
          <CardBody className="p-0">
            <table className="table table-sm table-striped mb-0 small">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Severity</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Target</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(a => (
                  <tr key={a.id}>
                    <td className="text-muted">{a.t}</td>
                    <td>
                      <Badge color="light" className="text-dark">
                        {a.severity || "—"}
                      </Badge>
                    </td>
                    <td>{a.actor}</td>
                    <td>
                      <code>{a.action}</code>
                    </td>
                    <td>{a.target}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardBody>
        </Card>
      </AsyncStateBoundary>
    </SuitePageShell>
  );
}

export function SecuritySuiteSection() {
  return (
    <Routes>
      <Route element={<SuiteLayout vertical="security" />}>
        <Route index element={<SecuritySocDashboard />} />
        <Route path="vulns" element={<VulnSeverityMatrixPage />} />
        <Route path="vulns/:id" element={<VulnDetailPage />} />
        <Route path="policies" element={<PoliciesCompliancePage />} />
        <Route path="audit" element={<AuditLogExplorerPage />} />
      </Route>
    </Routes>
  );
}
